import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star } from 'lucide-react';
import api from '@/lib/api';
import NewsletterForm from '@/components/NewsletterForm';
import FamilyMomentsSection from '@/components/FamilyMomentsSection';
import { Button } from '@/components/ui/button';

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, projectsRes, reviewsRes, servicesRes] = await Promise.all([
          api.get('/public/settings'),
          api.get('/public/projects?featured=true'),
          api.get('/public/reviews?featured=true'),
          api.get('/public/services')
        ]);
        setSettings(settingsRes.data);
        setFeaturedProjects(projectsRes.data.slice(0, 6));
        setReviews(reviewsRes.data.slice(0, 3));
        setServices(servicesRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  };

  const staggerContainer = {
    animate: { transition: { staggerChildren: 0.1 } }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  // Filtra le statistiche visibili
  const visibleStats = (settings?.stats || []).filter(s => s.is_visible).sort((a, b) => a.order - b.order);

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center" data-testid="hero-section">
        {/* Background Image/Video */}
        <div className="absolute inset-0 z-0">
          {settings?.hero_video ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            >
              <source src={settings.hero_video} />
            </video>
          ) : (
            <img
              src={settings?.hero_image || "https://images.unsplash.com/photo-1589124110662-198a360c458e?w=1920"}
              alt="Hero background"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Content */}
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <motion.div {...fadeInUp}>
            <span className="text-sm tracking-[0.3em] uppercase text-white/70 mb-4 block">
              {settings?.hero_subtitle || "Fotografo & Videomaker"}
            </span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-semibold mb-6"
            data-testid="hero-title"
          >
            {settings?.hero_title || "Andrea Caruso"}
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-accent text-xl sm:text-2xl lg:text-3xl text-white/90 mb-10 max-w-2xl mx-auto"
            data-testid="hero-tagline"
          >
            {settings?.hero_tagline || "Catturare l'essenza, raccontare l'emozione."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to={settings?.hero_cta_link || "/prenota"} data-testid="hero-cta-btn">
              <Button size="lg" className="bg-white text-black hover:bg-white/90 px-8 py-6 text-base">
                {settings?.hero_cta_text || "Prenota Ora"}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/portfolio" data-testid="hero-portfolio-btn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-base">
                Guarda Portfolio
              </Button>
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-2 bg-white/50 rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      {settings?.stats_section_visible !== false && visibleStats.length > 0 && (
        <section className="py-16 lg:py-24 px-6 bg-muted/30" data-testid="stats-section">
          <div className="max-w-[1800px] mx-auto">
            {settings?.stats_section_title && (
              <motion.div
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                variants={staggerContainer}
                className="text-center mb-12"
              >
                <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl">
                  {settings.stats_section_title}
                </motion.h2>
              </motion.div>
            )}

            <div className={`grid grid-cols-2 md:grid-cols-${Math.min(visibleStats.length, 4)} gap-8 lg:gap-16`}>
              {visibleStats.map((stat, index) => (
                <motion.div
                  key={stat.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="text-center"
                  data-testid={`stat-${index}`}
                >
                  <div className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold mb-2">
                    {stat.value}<span className="text-primary">{stat.suffix}</span>
                  </div>
                  <p className="text-muted-foreground text-sm sm:text-base">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Maternity & Newborn */}
      {settings?.family_section_visible !== false && <FamilyMomentsSection projects={featuredProjects} />}

      {/* Services Highlights */}
      {settings?.services_section_visible !== false && services.length > 0 && (
        <section className="py-24 lg:py-32 px-6" data-testid="services-section">
          <div className="max-w-[1800px] mx-auto">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.span variants={fadeInUp} className="text-sm tracking-[0.2em] uppercase text-muted-foreground">
                {settings?.services_section_subtitle || "Cosa Faccio"}
              </motion.span>
              <motion.h2 variants={fadeInUp} className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-4">
                {settings?.services_section_title || "Servizi"}
              </motion.h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="group border border-border p-8 lg:p-10 hover:border-foreground transition-colors duration-500"
                  data-testid={`service-card-${index}`}
                >
                  <h3 className="font-heading text-2xl lg:text-3xl mb-4">{service.title}</h3>
                  <p className="text-muted-foreground mb-6 line-clamp-3">{service.description}</p>
                  {service.starting_price && (
                    <p className="text-sm text-muted-foreground mb-6">
                      A partire da <span className="text-foreground font-medium">{service.starting_price}</span>
                    </p>
                  )}
                  <Link
                    to="/servizi"
                    className="inline-flex items-center text-sm font-medium link-underline"
                  >
                    Scopri di più
                    <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                  </Link>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <Link to="/servizi">
                <Button variant="outline" size="lg" data-testid="all-services-btn">
                  Tutti i Servizi
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Featured Projects */}
      {settings?.portfolio_section_visible !== false && featuredProjects.length > 0 && (
        <section className="py-24 lg:py-32 px-6 bg-muted/30" data-testid="featured-projects-section">
          <div className="max-w-[1800px] mx-auto">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.span variants={fadeInUp} className="text-sm tracking-[0.2em] uppercase text-muted-foreground">
                {settings?.portfolio_section_subtitle || "Portfolio"}
              </motion.span>
              <motion.h2 variants={fadeInUp} className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-4">
                {settings?.portfolio_section_title || "Lavori Selezionati"}
              </motion.h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                >
                  <Link
                    to={`/portfolio/${project.slug}`}
                    className="group block"
                    data-testid={`featured-project-${index}`}
                  >
                    <div className="aspect-[4/5] overflow-hidden bg-muted mb-4">
                      <img
                        src={project.cover_image || project.media?.[0]?.url}
                        alt={project.title}
                        className="w-full h-full object-cover img-zoom"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="font-heading text-xl mb-1 group-hover:text-muted-foreground transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{project.location}</p>
                  </Link>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <Link to="/portfolio">
                <Button size="lg" data-testid="all-portfolio-btn">
                  Vedi tutto il Portfolio
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {settings?.testimonials_section_visible !== false && reviews.length > 0 && (
        <section className="py-24 lg:py-32 px-6" data-testid="testimonials-section">
          <div className="max-w-[1800px] mx-auto">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.span variants={fadeInUp} className="text-sm tracking-[0.2em] uppercase text-muted-foreground">
                {settings?.testimonials_section_subtitle || "Testimonianze"}
              </motion.span>
              <motion.h2 variants={fadeInUp} className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-4">
                {settings?.testimonials_section_title || "Cosa Dicono di Me"}
              </motion.h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {reviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="bg-muted/50 p-8 lg:p-10"
                  data-testid={`testimonial-${index}`}
                >
                  <div className="flex gap-1 mb-6 star-rating">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        fill={i < review.rating ? 'currentColor' : 'none'}
                      />
                    ))}
                  </div>
                  <p className="font-accent text-lg italic mb-6 text-foreground/90">
                    "{review.text}"
                  </p>
                  <div>
                    <p className="font-medium">{review.name}</p>
                    <p className="text-sm text-muted-foreground">{review.event_service}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <Link to="/recensioni">
                <Button variant="outline" size="lg" data-testid="all-reviews-btn">
                  Tutte le Recensioni
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {settings?.cta_section_visible !== false && (
        <section className="py-24 lg:py-32 px-6 bg-foreground text-background" data-testid="cta-section">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-6"
            >
              {settings?.cta_title || "Pronto a raccontare la tua storia?"}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="font-accent text-xl text-background/70 mb-10 max-w-2xl mx-auto"
            >
              {settings?.cta_subtitle || "Ogni progetto è unico. Parliamo insieme di come posso aiutarti a creare qualcosa di speciale."}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to={settings?.cta_button_link || "/prenota"}>
                <Button size="lg" className="bg-background text-foreground hover:bg-background/90 px-8 py-6" data-testid="cta-book-btn">
                  {settings?.cta_button_text || "Prenota una Consulenza"}
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/contatti">
                <Button size="lg" variant="outline" className="border-background/50 text-background hover:bg-background/10 px-8 py-6" data-testid="cta-contact-btn">
                  Contattami
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Newsletter Section */}
      <section className="py-16 max-w-[1800px] mx-auto px-6 lg:px-12">
        <NewsletterForm settings={settings} />
      </section>
    </div>
  );
}
